import { blue, gray, green, orange, purple, red } from '@ant-design/colors';
import { notification } from 'antd';
import type { TotalizadorListData } from '@/components/totalizador/TotalizadorList';
import type { FormNotaFiscalValues } from '@/features/nfe/page/edicao/type/types';
import type { NotaFiscal } from '@/features/nfe/types/NotaFiscal';
import type { NotaFiscalPartial } from '@/features/nfe/types/NotaFiscalPartial';
import type { RootDispatch, RootState } from '@/state/store';
import type { ApiResponse, ApiResponseStatus } from '@/std/api/types';
import type { SortParams } from '@/types/interfaces';
import type { RelatoriosPayload } from '@/types/interfaces/RelatoriosPayload';
import {
    canUserListAllVendedores,
    getEmpresa,
    getEmpresaIdpk,
    getLogin,
} from '@/features/login/util';
import { marcadoresToStringIdpk } from '@/features/marcador/util';
import { useNotaFiscalDrawerCompartilhar } from '@/features/nfe/page/drawers/DrawerCompartilhar';
import { useNotaFiscalStore } from '@/features/nfe/store';
import { NotaFiscalStatus } from '@/features/nfe/types/enum';
import { type BuildUrlParams, buildUrl } from '@/std/api/buildUrl';
import { comTokenGet, comTokenPost, comTokenPut, comTokenRemove } from '@/std/api/comToken';
import { throwIfResponseIsErr } from '@/std/api/util';
import { base64ToStr } from '@/std/base64';
import { registrosToCsv } from '@/std/csv';
import { downloadFile, downloadTextFile } from '@/std/download';
import { openPDF } from '@/std/pdf';
import { endReduxFnError, endReduxFnOk, startReduxFn } from '@/std/redux';
import { TipoConsulta } from '@/types/enum';
import { Endpoint } from '@/types/enum/endpoint';
import type { TCalcImpostosItens, TValorTotaisNf } from './types';

export const effects = (dispatch: RootDispatch) => ({
    async calcularImpostos(payload: TCalcImpostosItens, state: RootState): Promise<void> {
        dispatch.notaFiscal.setState({
            calcularImpostos: startReduxFn(state.notaFiscal.calcularImpostos.data),
        });

        try {
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NotaFiscalCalcularImpostos, params);
            const response = await comTokenPost(url, payload);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                calcularImpostos: endReduxFnOk(response.data.registros?.[0]),
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                calcularImpostos: endReduxFnError(error),
            });
        }
    },

    async cancelarNotaFiscal(payload: {
        nof_idpk: number;
        justificativa: string;
        cancelar_venda?: 'S';
        cancelar_bonificacao?: 'S';
        updateTable?: boolean;
    }): Promise<void> {
        dispatch.notaFiscal.setState({
            cancelarNotaFiscal: startReduxFn(),
        });

        try {
            const { nof_idpk, updateTable, ...rest } = payload;

            const params = {
                ...rest,
                empresa_idpk: getEmpresaIdpk(),
            };

            const url = buildUrl(Endpoint.NotaFiscalCancelar, params, nof_idpk);
            const response = await comTokenPost(url);
            throwIfResponseIsErr(response);

            const { data } = response;

            if (data) {
                const selectedRow = useNotaFiscalStore.getState().selectedRow;

                if (selectedRow) {
                    const newSelectedRow = structuredClone(selectedRow) as NotaFiscalPartial;

                    newSelectedRow.nof_nfe_pdf = data.evento_pdf;
                    newSelectedRow.nof_nfe_xml = data.evento_xml;

                    useNotaFiscalStore.setState({
                        selectedRow: newSelectedRow,
                    });
                }
            }

            dispatch.notaFiscal.setState({
                cancelarNotaFiscal: endReduxFnOk(data),
            });

            if (updateTable) {
                void dispatch.notaFiscal.get({});
            }
        } catch (error) {
            dispatch.notaFiscal.setState({
                cancelarNotaFiscal: endReduxFnError(error),
            });
        }
    },

    async compartilharSend(payload: {
        idpk: number;
        tipo: string;
        template_id: string;
        tags?: { nome: string; valor?: string | null }[];
        fin_email?: string | null;
        fin_email_cc?: string | null;
    }): Promise<void> {
        dispatch.notaFiscal.setState({
            compartilharSend: startReduxFn(),
        });

        const { idpk, tags, fin_email, fin_email_cc, ...rest } = payload;

        const params = {
            empresa_idpk: getEmpresaIdpk(),
            ...rest,
        };
        const bodySend = payload.tipo !== 'W' ? { fin_email, fin_email_cc, tags } : undefined;

        const url = buildUrl(Endpoint.NotaFiscalCompartilhar, params, idpk);

        try {
            const response = await comTokenPost(url, bodySend);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                compartilharSend: endReduxFnOk(undefined),
            });

            useNotaFiscalDrawerCompartilhar.getState().actions.close();

            if (payload.tipo === 'E') {
                notification.success({
                    description:
                        'Atenção, a notificação pode demorar até 5 minutos para ser entregue.',
                    title: 'Email enviado',
                });
            }
        } catch (error) {
            dispatch.notaFiscal.setState({
                compartilharSend: endReduxFnError(error),
            });

            if (payload.tipo === 'E') {
                notification.error({
                    description: error.message,
                    title: 'Falhou!',
                });
            }
        }
    },

    async enviarExpedicao(payload: { nof_idpk: number }, state: RootState): Promise<void> {
        const { getTable } = state.notaFiscal;
        const { nof_idpk } = payload;

        dispatch.notaFiscal.setState({
            enviarExpedicao: startReduxFn(),
            getTable: {
                ...getTable,
                loadingTable: true,
            },
        });

        try {
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NotaFiscalEnviarExpedicao, params, nof_idpk);
            const response: ApiResponse = await comTokenPut(url);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                enviarExpedicao: endReduxFnOk(undefined),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });

            void dispatch.notaFiscal.get({});
        } catch (error) {
            dispatch.notaFiscal.setState({
                enviarExpedicao: endReduxFnError(error),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });
        }
    },

    async estornarEstoque(
        payload: { nof_idpk: number; updateTable?: boolean },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            estornarEstoque: startReduxFn(),
            getTable: {
                ...getTable,
                loadingTable: true,
            },
        });

        const { nof_idpk, updateTable } = payload;
        const params = {
            empresa_idpk: getEmpresaIdpk(),
            // única diferença entre esta e a fn lançar
            estornar: 'S',
        };

        const url = buildUrl(Endpoint.NotaFiscalEstoque, params, nof_idpk);

        try {
            const response = await comTokenPut(url);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                estornarEstoque: endReduxFnOk(undefined),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });

            if (updateTable) {
                void dispatch.notaFiscal.get({});
            }
        } catch (error) {
            dispatch.notaFiscal.setState({
                estornarEstoque: endReduxFnError(error),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async estornarFinanceiro(
        payload: { nof_idpk: number; updateTable?: boolean },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            estornarFinanceiro: startReduxFn(),
            getTable: {
                ...getTable,
                loadingTable: true,
            },
        });

        const { nof_idpk, updateTable } = payload;
        const params = {
            empresa_idpk: getEmpresaIdpk(),
            // única diferença entre esta e a fn lançar
            estornar: 'S',
        };

        const url = buildUrl(Endpoint.NotaFiscalFinanceiro, params, nof_idpk);

        try {
            const response = await comTokenPut(url);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                estornarFinanceiro: endReduxFnOk(undefined),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });

            if (updateTable) {
                void dispatch.notaFiscal.get({});
            }
        } catch (error) {
            dispatch.notaFiscal.setState({
                estornarFinanceiro: endReduxFnError(error),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async exportarCSV(): Promise<void> {
        const selectedRows = useNotaFiscalStore.getState().selectedRows;

        if (selectedRows.length === 0) {
            return;
        }

        const where = `(nof_idpk in (${selectedRows.map((row) => row.nof_idpk).join(',')}))`;

        try {
            const params = {
                empresa_idpk: getEmpresaIdpk(),
                fields: `
                    nof_numero,
                    nof_serie,
                    nof_data_emissao,
                    nof_total_nota,volume, 
                    nof_cliente_idpk,
                    nof_dest_razao_social,
                    nof_dest_cidade,
                    nof_dest_uf,
                    nof_transp_nome,
                    nof_usuario_nome,
                    nof_status,
                    nof_total_frete
                `,
                where,
            };

            const url = buildUrl(Endpoint.NotaFiscal, params);
            const response: ApiResponse = await comTokenGet(url);
            throwIfResponseIsErr(response);

            if (!response.data.registros) {
                return;
            }

            // biome-ignore assist/source/useSortedKeys: ordem personalizada
            const csvData = response.data.registros.map((row) => ({
                Cidade: row.nof_dest_cidade,
                Cliente: row.nof_cliente_idpk,
                Lançamento: row.nof_data_emissao,
                Nome: row.nof_dest_razao_social,
                'Nome Transportador': row.nof_transp_nome,
                Frete: row.nof_total_frete,
                'Nro Nota': `${row.nof_numero ?? 0}/${row.nof_serie}`,
                'Peso Bruto': row.volume?.nfv_peso_bruto,
                'Peso Liq.': row.volume?.nfv_peso_liquido,
                Status: NotaFiscalStatus[row.nof_status],
                Total: row.nof_total_nota,
                UF: row.nof_dest_uf,
                Vendedor: row.nof_usuario_nome,
                Volumes: row.volume?.nfv_quantidade,
            }));

            await registrosToCsv(csvData, 'tabela-notas-fiscais');
        } catch (e) {
            console.error(e);
        }
    },

    async gerarRelatorio(
        payload: { link: string; open: boolean },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            gerarRelatorio: startReduxFn(),
            getTable: {
                ...getTable,
                loadingTable: true,
            },
        });

        const { link, open } = payload;

        try {
            if (open) {
                openPDF({
                    authenticated: true,
                    autoPrint: true,
                    link,
                    type: 'link',
                });
            } else {
                await downloadFile(link, 'notafiscal.xml');
            }

            dispatch.notaFiscal.setState({
                gerarRelatorio: endReduxFnOk(undefined),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                gerarRelatorio: endReduxFnError(error),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async gerarRelatorioMultiple(payload: number[], state: RootState): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            gerarRelatorioMultiple: startReduxFn(),
            getTable: {
                ...getTable,
                loadingTable: true,
            },
        });

        const params = {
            empresa_idpk: getEmpresaIdpk(),
            gerar_fatura: 'S',
            nota_fiscal_list: JSON.stringify(payload),
        };

        const url = buildUrl(Endpoint.NotaFiscalGerarDanfes, params);

        try {
            const response = await comTokenPost(url);
            throwIfResponseIsErr(response);

            openPDF({
                authenticated: true,
                relatorios: response.data as RelatoriosPayload,
                type: 'file',
            });

            dispatch.notaFiscal.setState({
                gerarRelatorioMultiple: endReduxFnOk(undefined),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                gerarRelatorioMultiple: endReduxFnError(error),
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },
    async get(
        payload: {
            pesquisar?: string;
            data_inicio?: string;
            data_fim?: string;
            total_registros?: string;
            registro_inicial?: number;
            qtde_registros?: number;
            sort?: SortParams;
            tipo_consulta?: TipoConsulta;
        },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable, get } = notaFiscal;

        dispatch.notaFiscal.setState({
            get: startReduxFn(get.data),
            getTable: {
                ...getTable,
                updateTable: false,
            },
        });

        try {
            const {
                filterDateRange,
                filterPesquisar,
                filterTipo,
                filterFornecedor,
                filterCliente,
                filterNumero,
                filterSerie,
                filterStatus,
                filterVendedor,
                filterCidade,
                filterMarcador,
                filterVendaExterna,
                filterProdutos,
                filterAcao,
                filterSistemasIntegrados,
                registroInitial,
                qtdRegistros,
                sortParams,
                pagination,
                resetPagination,
                filterFormasPagamento,
            } = getTable;

            const {
                pesquisar,
                total_registros,
                registro_inicial,
                qtde_registros,
                sort,
                tipo_consulta = TipoConsulta.Resumida,
                ...restPayload
            } = payload;

            // MONTANDO OS PARAMETROS OBRIGATÓRIOS
            const params: BuildUrlParams = {
                acao: filterAcao,
                ambiente: getEmpresa()?.emp_fiscal_ambiente,
                cliente_idpk: filterCliente?.idpk,
                data_fim: filterDateRange?.end.format('DD-MM-YYYY'),
                data_inicio: filterDateRange?.start.format('DD-MM-YYYY'),
                destinatario_cidade: filterCidade?.nome,
                empresa_idpk: getEmpresaIdpk(),
                forma_pagamento_idpk: filterFormasPagamento?.map((f) => f.fop_idpk),
                fornecedor_idpk: filterFornecedor?.idpk,
                marcadores: marcadoresToStringIdpk(filterMarcador),
                numero: filterNumero,
                orderby: 'nof_data_emissao desc, nof_idpk desc',
                pesquisar: pesquisar || filterPesquisar,
                produto_idpk: filterProdutos?.map((v) => v.pro_idpk),
                qtde_registros: qtde_registros || qtdRegistros,
                registro_inicial:
                    registro_inicial !== null && registro_inicial !== undefined
                        ? registro_inicial
                        : registroInitial,
                serie: filterSerie,
                sistema_integrado_idpk: filterSistemasIntegrados?.map((s) => s.sistema_idpk),
                status: filterStatus ? JSON.stringify(filterStatus) : undefined,
                tipo: filterTipo?.idpk,
                tipo_consulta,
                total_registros,
                venda_externa_idpk: filterVendaExterna?.idpk,
                vendedor_idpk: canUserListAllVendedores()
                    ? filterVendedor?.idpk
                    : getLogin()?.usu_idpk,
                ...restPayload,
            };

            // CASO TENHA QUE ORDENAR OS DADOS (*OPCIONAL)
            if (sort?.shouldSort || sortParams?.shouldSort) {
                params.orderby = `${sort?.fieldName || sortParams?.fieldName}${
                    sort?.orderDirection || sortParams?.orderDirection
                }`;
            }

            // SE MUDAR O FILTRO OU PRECISAR RESETAR A PAGINAÇÃO
            if (resetPagination) {
                params.registro_inicial = 0;
                params.total_registros = 'S';
            }

            const url = buildUrl(Endpoint.NotaFiscal, params);
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [], total_registros: totalRegistroResponse = null } = {} } =
                response;

            dispatch.notaFiscal.setState({
                get: endReduxFnOk(registros),
                getTable: {
                    ...getTable,
                    updateTable: false,
                    ...((totalRegistroResponse || totalRegistroResponse === 0) && {
                        totalRegistrosTable: totalRegistroResponse,
                    }),
                    // SE MUDAR O FILTRO OU PRECISAR RESETAR A PAGINAÇÃO
                    ...(resetPagination && {
                        registro_inicial: 0,
                    }),
                    pagination: {
                        ...pagination,
                        // SE MUDAR O FILTRO OU PRECISAR RESETAR A PAGINAÇÃO
                        ...(resetPagination && {
                            current: 1,
                        }),
                        // SE PRECISAR ATUALIZAR A PÁGINA É FEITO AQUI
                        ...(registro_inicial !== null &&
                            registro_inicial !== undefined && {
                                current: registro_inicial / (qtde_registros || qtdRegistros) + 1,
                            }),
                        // SE PRECISAR ATUALIZAR OS TOTAIS É FEITO AQUI
                        ...((totalRegistroResponse || totalRegistroResponse === 0) && {
                            showSizeChanger: totalRegistroResponse > 10,
                            showTotal: () => `Total de Registros: ${totalRegistroResponse}`,
                            total: totalRegistroResponse,
                        }),
                    },
                    resetPagination: false,
                },
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                get: endReduxFnError(error),
            });
        }
    },

    async getOne(payload: { nof_idpk: number }, state: RootState): Promise<NotaFiscal | null> {
        dispatch.notaFiscal.setState({
            getOne: startReduxFn(state.notaFiscal.getOne.data),
        });

        const { nof_idpk } = payload;
        const params = { empresa_idpk: getEmpresaIdpk() };

        const url = buildUrl(Endpoint.NotaFiscal, params, nof_idpk);

        try {
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [] } = {} } = response;

            const returnGetOne = registros && registros.length > 0 ? registros[0] : null;

            dispatch.notaFiscal.setState({
                getOne: endReduxFnOk(returnGetOne),
            });

            return returnGetOne;
        } catch (error) {
            dispatch.notaFiscal.setState({
                getOne: endReduxFnError(error),
            });

            return null;
        }
    },

    async getReceitaFiscal(
        payload: { nof_idpk: number },
        state: RootState,
    ): Promise<{ status: ApiResponseStatus; mensagem: string }> {
        dispatch.notaFiscal.setState({
            getReceitaFiscal: startReduxFn(state.notaFiscal.getReceitaFiscal.data),
        });

        try {
            const { nof_idpk } = payload;
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NotaFiscalConsultar, params, nof_idpk);
            const response = await comTokenPost(url);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                getReceitaFiscal: endReduxFnOk(response.data.mensagem),
            });

            return { mensagem: response.data.mensagem, status: 'sucesso' };
        } catch (error) {
            dispatch.notaFiscal.setState({
                getReceitaFiscal: endReduxFnError(error),
            });

            return { mensagem: `${error}`, status: 'erro' };
        }
    },

    async lancarEstoque(
        payload: { nof_idpk: number; updateTable?: boolean },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            getTable: {
                ...getTable,
                loadingTable: true,
            },
            lancarEstoque: startReduxFn(),
        });

        const { nof_idpk, updateTable } = payload;
        const params = {
            empresa_idpk: getEmpresaIdpk(),
            // única diferença entre esta e a fn estornar
            movimentar: 'S',
        };

        const url = buildUrl(Endpoint.NotaFiscalEstoque, params, nof_idpk);

        try {
            const response = await comTokenPut(url);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                lancarEstoque: endReduxFnOk(undefined),
            });

            if (updateTable) {
                void dispatch.notaFiscal.get({});
            }
        } catch (error) {
            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                lancarEstoque: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async lancarFinanceiro(
        payload: { nof_idpk: number; updateTable?: boolean },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            getTable: {
                ...getTable,
                loadingTable: true,
            },
            lancarFinanceiro: startReduxFn(),
        });

        const { nof_idpk, updateTable } = payload;
        const params = {
            empresa_idpk: getEmpresaIdpk(),
            // única diferença entre esta e a fn estornar
            movimentar: 'S',
        };

        const url = buildUrl(Endpoint.NotaFiscalFinanceiro, params, nof_idpk);

        try {
            const response = await comTokenPut(url);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                lancarFinanceiro: endReduxFnOk(undefined),
            });

            if (updateTable) {
                void dispatch.notaFiscal.get({});
            }
        } catch (error) {
            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                lancarFinanceiro: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async post(payload: { body: FormNotaFiscalValues }, state: RootState): Promise<number | null> {
        dispatch.notaFiscal.setState({
            post: startReduxFn(state.notaFiscal.post.data),
        });

        try {
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NotaFiscal, params);
            const response = await comTokenPost(url, payload.body);
            throwIfResponseIsErr(response);

            const nof_idpk: number = response.data.registros?.[0]
                ? response.data.registros[0].nof_idpk
                : 0;

            dispatch.notaFiscal.setState({
                post: endReduxFnOk({ nof_idpk }),
            });

            notification.success({
                description: 'Nota Fiscal cadastrada',
                title: 'Feito!',
            });

            return nof_idpk;
        } catch (error) {
            dispatch.notaFiscal.setState({
                post: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Não foi possível cadastrar a nota fiscal!',
            });

            return null;
        }
    },

    async preVisualizarDanfe(
        payload: { nof_idpk: number; tipo: number },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            getTable: {
                ...getTable,
                loadingTable: true,
            },
            preVisualizarDanfe: startReduxFn(),
        });

        try {
            const { nof_idpk, tipo } = payload;
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NotaFiscalPreVisualizar, params, nof_idpk);
            const response = await comTokenPost(url);
            throwIfResponseIsErr(response);

            const { data: { xml = {} } = {} } = response || {};
            const { file_base64: base64Xml = '', file_name: fileNameXml = '' } = xml || {};

            if (tipo !== 2) {
                openPDF({
                    authenticated: true,
                    relatorios: response.data as RelatoriosPayload,
                    type: 'file',
                });
            } else {
                const text = base64ToStr(base64Xml);
                await downloadTextFile(text, fileNameXml);
            }

            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                preVisualizarDanfe: endReduxFnOk(undefined),
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                preVisualizarDanfe: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async put(
        payload: {
            nof_idpk: number;
            body: FormNotaFiscalValues;
        },
        state: RootState,
    ): Promise<void> {
        dispatch.notaFiscal.setState({
            put: startReduxFn(state.notaFiscal.put.data),
        });

        try {
            const params = { empresa_idpk: getEmpresaIdpk() };
            const { nof_idpk, body } = payload;

            const url = buildUrl(Endpoint.NotaFiscal, params, nof_idpk);
            const response = await comTokenPut(url, body);
            throwIfResponseIsErr(response);

            if (response.data.status === 'sucesso') {
                dispatch.notaFiscal.setState({
                    put: endReduxFnOk('Success'),
                });

                notification.success({
                    description: 'Nota Fiscal atualizada',
                    title: 'Feito!',
                });

                return response.data;
            }

            dispatch.notaFiscal.setState({
                put: endReduxFnOk('Error'),
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                put: endReduxFnError(error),
            });

            notification.error({
                description: error.message,
                title: 'Não foi possível atualizar a nota fiscal!',
            });
        }
    },

    async remove(
        payload: { nof_idpk: number; updateTable?: boolean },
        state: RootState,
    ): Promise<void> {
        const { notaFiscal } = state;
        const { getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            getTable: {
                ...getTable,
                loadingTable: true,
            },
            remove: startReduxFn(state.notaFiscal.remove.data),
        });

        try {
            const { nof_idpk, updateTable } = payload;
            const params = { empresa_idpk: getEmpresaIdpk() };
            const url = buildUrl(Endpoint.NotaFiscal, params, nof_idpk);
            const response = await comTokenRemove(url);
            throwIfResponseIsErr(response);

            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                remove: endReduxFnOk('Success'),
            });

            if (updateTable) {
                const {
                    pagination,
                    pagination: { total = 0 },
                    registroInitial,
                } = getTable;

                let isLastPageOnlyOneRegister = false;

                // VERIFICA SE É A ÚLTIMA PÁGINA E TEM APENAS UM ITEM PARA PODER MUDAR DE PÁGINA APÓS DELETAR
                if (total && registroInitial && total - 1 === registroInitial) {
                    isLastPageOnlyOneRegister = true;
                    void dispatch.notaFiscal.get({
                        registro_inicial: registroInitial - (pagination?.pageSize || 0),
                        total_registros: 'S',
                    });

                    void dispatch.notaFiscal.totalizador({});
                }

                if (!isLastPageOnlyOneRegister) {
                    void dispatch.notaFiscal.get({
                        total_registros: 'S',
                    });

                    void dispatch.notaFiscal.totalizador({});
                }
            }

            notification.success({
                description: 'Nota Fiscal excluída.',
                title: 'Feito!',
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                getTable: {
                    ...getTable,
                    loadingTable: false,
                },
                remove: endReduxFnError(error),
            });

            notification.error({
                description: String(error),
                title: 'Falhou!',
            });
        }
    },

    async totalizador(_, state: RootState): Promise<void> {
        const { notaFiscal } = state;
        const { totalizador, getTable } = notaFiscal;

        dispatch.notaFiscal.setState({
            totalizador: startReduxFn(totalizador.data),
        });

        const {
            filterDateRange,
            filterPesquisar,
            filterTipo,
            filterFornecedor,
            filterCliente,
            filterNumero,
            filterSerie,
            filterVendedor,
            filterCidade,
            filterMarcador,
            filterVendaExterna,
            filterProdutos,
            filterAcao,
            filterSistemasIntegrados,
            filterFormasPagamento,
        } = getTable;

        const ambiente = getEmpresa()?.emp_fiscal_ambiente;

        const params: BuildUrlParams = {
            acao: filterAcao,
            ambiente,
            data_fim: filterDateRange?.end.format('DD-MM-YYYY'),
            data_inicio: filterDateRange?.start.format('DD-MM-YYYY'),
            empresa_idpk: getEmpresaIdpk(),
            forma_pagamento_idpk: filterFormasPagamento?.map((f) => f.fop_idpk),
            marcadores: marcadoresToStringIdpk(filterMarcador),
            pesquisar: filterPesquisar,
            produto_idpk: filterProdutos?.map((v) => v.pro_idpk),
            sistema_integrado_idpk: filterSistemasIntegrados?.map((s) => s.sistema_idpk),
            venda_externa_idpk: filterVendaExterna?.idpk,
        };

        if (filterTipo) {
            params.tipo = filterTipo.idpk;
        }

        if (!canUserListAllVendedores()) {
            params.vendedor_idpk = getLogin()?.usu_idpk;
        } else if (filterVendedor) {
            params.vendedor_idpk = filterVendedor.idpk;
        }

        if (filterCidade) {
            params.destinatario_cidade = filterCidade?.nome;
        }

        if (filterNumero) {
            params.numero = filterNumero;
        }

        if (filterSerie) {
            params.serie = filterSerie;
        }

        // filtros que usam param 'where'
        let where = '';

        if (filterFornecedor) {
            where = `nof_fornecedor_idpk=${filterFornecedor.idpk}`;
        }

        if (filterCliente) {
            where = `${where.length > 0 ? `${where} AND ` : ''}nof_cliente_idpk=${filterCliente.idpk}`;
        }

        if (where.length > 0) {
            params.where = where;
        }

        const url = buildUrl(Endpoint.NotaFiscalTotais, params);

        try {
            const response = await comTokenGet(url);
            throwIfResponseIsErr(response);

            const { data: { registros = [] } = {} } = response;

            const cores = {
                Aguardando: blue.primary,
                Cancelada: red.primary,
                Emitida: green.primary,
                Estornada: gray.primary,
                Incorreta: orange.primary,
                Rejeitada: purple.primary,
                Total: 'black',
            };

            const totais: TotalizadorListData[] = registros.map((total: TValorTotaisNf) => ({
                color: cores[total.tipo],
                quantity: total.quantidade_total,
                title: total.tipo,
                total: total.valor_total,
                value: total.tipo,
            }));

            dispatch.notaFiscal.setState({
                totalizador: endReduxFnOk(totais),
            });
        } catch (error) {
            dispatch.notaFiscal.setState({
                totalizador: endReduxFnError(error),
            });
        }
    },
});
